/**
 * Replacement milestones — V103-4-5 / V103-10-6 (ADR-0069 §4 and owner
 * decision 3: planned-change work is milestones, never POA&M items).
 *
 *   - A SINGLE-component replacement (from a boundary drift row, e.g. an
 *     unverified sub-processor that caps GovRAMP at Provisional) previews what
 *     changes per program and adds its milestones as work items
 *     (`source_kind = 'component_replacement'`, `source_id` = the replaced
 *     component). Nothing else is written: the boundary changes only when a
 *     person edits it (or a transition cuts over).
 *   - Milestones are keyed by COMPONENT and LOSS: `rep:<component>:loss:<requirement>`,
 *     `rep:<component>:switch`, `rep:<component>:stand_up`. A full move
 *     (planTransition) derives the same keys from its gap, so a replacement
 *     planned earlier carries into the move as "already planned": the open work
 *     item is linked, not duplicated, and keeps its earlier date (build
 *     acceptance #5).
 *   - The gap view projects the move's derived milestones without creating
 *     any (`projectTransitionMilestones`): new · already planned · planned, and
 *     "not needed" for an earlier replacement whose component the move now keeps
 *     (build acceptance #3).
 *
 * Every query filters by the caller's workspace id; another workspace's id is
 * not_found. Writes are a person's (no `system:` actor).
 */
import { getProgramManifest } from '../programs/manifests.js';
import { BoundaryError } from './boundary-service.js';
import { listBoundaryComponents } from './boundary-component-service.js';
import { listResponsibilities } from './responsibility-service.js';
import { resolveDerivedOwner } from './derived-work-service.js';
import { createWorkItem, WorkItemDuplicateError } from './work-item-service.js';
import { assertAssignable } from './assignment-service.js';
export const REPLACEMENT_SOURCE_KIND = 'component_replacement';
/** The shared dedupe key: one open work item per component × loss (or switch / stand-up). */
export function replacementDedupeKey(componentId, kind, requirementId) {
    return `rep:${componentId}:${kind}${kind === 'loss' && requirementId ? `:${requirementId}` : ''}`;
}
/** "nist-800-53:si-4" → "SI-4"; a KSI id keeps its case. */
export function requirementLabel(requirementId) {
    const tail = requirementId.split(':').pop() ?? requirementId;
    return /^[a-z]{2}-\d/.test(tail) ? tail.toUpperCase() : tail;
}
const requirementOf = (objectiveId) => objectiveId.split('_obj')[0];
const DAY = 86_400_000;
const dayIso = (d) => `${d.toISOString().slice(0, 10)}T00:00:00.000Z`;
const serviceOf = (c) => c?.external_service_id ?? c?.inherited_from_service_id ?? null;
/**
 * The milestones a transition's gap implies for one program: a switch per
 * replaced component (and per removed component whose provider leaves), and a
 * loss per requirement whose provider-held objectives come back to the
 * customer, attributed to the component that used that provider. Pure.
 */
export function gapMilestones(gap, components, names) {
    const label = getProgramManifest(gap.program)?.shortLabel ?? gap.program;
    const removedTpr = new Set(gap.tprs.removed.map((t) => t.external_service_id));
    const out = [];
    // Components that leave (replaced or removed), with the provider each used.
    const leaving = [];
    for (const row of gap.components) {
        if (row.change === 'replaced' && row.replaces)
            leaving.push({ id: row.replaces.component_id, key: row.replaces.component_key, svc: serviceOf(components.get(row.replaces.component_id)), to: row.component_key });
        if (row.change === 'removed')
            leaving.push({ id: row.component_id, key: row.component_key, svc: serviceOf(components.get(row.component_id)), to: null });
    }
    leaving.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    for (const l of leaving) {
        const svcName = l.svc && removedTpr.has(l.svc) ? names.get(l.svc) ?? null : null;
        if (!l.to && !svcName)
            continue; // a removed component whose provider stays needs no switch
        out.push({
            key: `switch:${l.id}`, dedupe_key: replacementDedupeKey(l.id, 'switch'), profile_id: null, kind: 'switch',
            component_id: l.id, component_key: l.key, requirement_id: null,
            title: svcName ? (l.to ? `Move data off ${svcName} and switch ${l.key} to ${l.to}` : `Move data off ${svcName} before ${l.key} leaves`) : `Switch ${l.key} to ${l.to}`,
            detail: 'Customer work outside GAC Pro; mark it done when the switch is made.',
        });
    }
    const lost = new Map();
    for (const o of gap.objectives) {
        if (o.change !== 'lost')
            continue;
        const reqId = o.requirement_id || requirementOf(o.objective_id);
        if (lost.has(reqId))
            continue;
        const by = leaving.find((l) => l.svc && l.svc === o.service_before) ?? null;
        lost.set(reqId, { component: by ? { id: by.id, key: by.key } : null, svc: o.service_before });
    }
    for (const [reqId, l] of [...lost.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
        const cid = l.component?.id ?? `svc-${l.svc ?? 'none'}`;
        const svcName = l.svc ? names.get(l.svc) ?? null : null;
        out.push({
            key: `loss:${cid}:${reqId}`, dedupe_key: replacementDedupeKey(cid, 'loss', reqId), profile_id: gap.profile_id, kind: 'loss',
            component_id: cid, component_key: l.component?.key ?? svcName ?? reqId, requirement_id: reqId,
            title: `Own ${requirementLabel(reqId)}${svcName ? ` (was ${svcName})` : ''} · ${label}`,
            detail: `${requirementLabel(reqId)} stops being provider-held after the move; you hold it from cutover.`,
        });
    }
    return out;
}
function parseKey(dedupe) {
    const m = /^rep:([^:]+):(loss|switch|stand_up)(?::(.+))?$/.exec(dedupe);
    return m ? { component_id: m[1], kind: m[2], requirement_id: m[3] ?? null } : null;
}
/** Open milestones from single-component replacements (any component, or one). */
export async function listReplacementItems(db, workspaceId, componentId) {
    const r = await db.query(`SELECT work_item_id, dedupe_key, title, due_at, status FROM work_items
      WHERE workspace_id = ? AND source_kind = ? AND status = 'open' AND dedupe_key IS NOT NULL ${componentId ? 'AND source_id = ?' : ''}
      ORDER BY COALESCE(due_at, '9999'), work_item_id`, componentId ? [workspaceId, REPLACEMENT_SOURCE_KIND, componentId] : [workspaceId, REPLACEMENT_SOURCE_KIND]);
    const owners = await db.query(`SELECT a.subject_id, a.user_id FROM assignments a JOIN work_items w ON w.work_item_id = a.subject_id AND w.workspace_id = a.workspace_id
      WHERE a.workspace_id = ? AND w.source_kind = ? AND w.status = 'open' AND a.subject_kind = 'work_item' AND a.responsibility = 'owner' AND a.effective_to IS NULL`, [workspaceId, REPLACEMENT_SOURCE_KIND]);
    return r.results.flatMap((w) => {
        const k = parseKey(w.dedupe_key);
        return k ? [{ ...w, ...k, owner_user_ids: owners.results.filter((o) => o.subject_id === w.work_item_id).map((o) => o.user_id) }] : [];
    });
}
/** The open work item holding a dedupe key, if any (any source: a replacement or an earlier transition). */
export async function openItemByDedupe(db, workspaceId, dedupe) {
    const r = await db.query(`SELECT work_item_id, due_at, source_kind FROM work_items WHERE workspace_id = ? AND dedupe_key = ? AND status = 'open' LIMIT 1`, [workspaceId, dedupe]);
    return r.results[0] ?? null;
}
/**
 * What the move's gap implies for milestones, without creating any. A
 * replacement planned earlier for a component the move now KEEPS is listed as
 * `not_needed` (its work item stays open until a person closes it).
 */
export async function projectTransitionMilestones(db, workspaceId, transitionId, gap, currentComponentIds) {
    const components = new Map((await listBoundaryComponents(db, workspaceId, { includeRemoved: true })).map((c) => [c.component_id, c]));
    const names = new Map((await db.query(`SELECT external_service_id, name FROM external_services WHERE workspace_id = ?`, [workspaceId]))
        .results.map((s) => [s.external_service_id, s.name]));
    const planned = (await db.query(`SELECT milestone_key, profile_id, work_item_id, scheduled_for FROM transition_plan_items WHERE workspace_id = ? AND transition_id = ? AND ended_at IS NULL`, [workspaceId, transitionId])).results;
    const out = [];
    const derived = gapMilestones(gap, components, names);
    for (const m of derived) {
        const base = { key: m.key, profile_id: m.profile_id, kind: m.kind, component_id: m.component_id, component_key: m.component_key, requirement_id: m.requirement_id, title: m.title };
        const p = planned.find((x) => x.milestone_key === m.key && (x.profile_id ?? null) === m.profile_id);
        if (p) {
            out.push({ ...base, status: 'planned', work_item_id: p.work_item_id, due_at: p.scheduled_for });
            continue;
        }
        const open = await openItemByDedupe(db, workspaceId, m.dedupe_key);
        out.push(open ? { ...base, status: 'already_planned', work_item_id: open.work_item_id, due_at: open.due_at } : { ...base, status: 'new', work_item_id: null, due_at: null });
    }
    // An earlier replacement of a component this program keeps unchanged: not needed by the move.
    const kept = new Set(gap.components.filter((c) => c.change === 'unchanged').map((c) => c.component_id));
    const inScope = new Set(currentComponentIds);
    const taken = new Set(out.map((o) => o.work_item_id).filter(Boolean));
    for (const it of await listReplacementItems(db, workspaceId)) {
        if (taken.has(it.work_item_id) || !kept.has(it.component_id) || !inScope.has(it.component_id))
            continue;
        const c = components.get(it.component_id);
        out.push({
            key: `${it.kind}:${it.component_id}${it.requirement_id ? `:${it.requirement_id}` : ''}`, profile_id: it.kind === 'loss' ? gap.profile_id : null, kind: it.kind,
            component_id: it.component_id, component_key: c?.component_key ?? it.component_id, requirement_id: it.requirement_id, title: it.title,
            status: 'not_needed', work_item_id: it.work_item_id, due_at: it.due_at,
        });
    }
    return out;
}
const verifiedForGovramp = (s) => !!s && ((s.govramp_status === 'authorized' && !!s.govramp_status_checked_at) || s.fedramp_status === 'authorized');
async function openComponent(db, ws, id, what) {
    const c = (await listBoundaryComponents(db, ws)).find((x) => x.component_id === id);
    if (!c)
        throw new BoundaryError('not_found', `${what} not found.`);
    return c;
}
/** What replacing one component changes in every program whose standing boundary holds it, and the milestones it needs. */
export async function previewComponentReplacement(db, input) {
    const ws = input.workspace_id;
    const now = input.now ?? new Date();
    const comp = await openComponent(db, ws, input.component_id, 'Component');
    const repl = input.replacement_component_id ? await openComponent(db, ws, input.replacement_component_id, 'Replacement component') : null;
    if (repl && repl.component_id === comp.component_id)
        throw new BoundaryError('invalid', 'A component cannot replace itself.');
    const services = new Map((await db.query(`SELECT external_service_id, name, fedramp_status, govramp_status, govramp_status_checked_at FROM external_services WHERE workspace_id = ?`, [ws])).results
        .map((s) => [s.external_service_id, s]));
    const all = await listBoundaryComponents(db, ws);
    const byId = new Map(all.map((c) => [c.component_id, c]));
    const scope = (await db.query(`SELECT s.profile_id, s.component_id, p.program FROM boundary_component_scope s
       JOIN certification_profiles p ON p.profile_id = s.profile_id AND p.workspace_id = s.workspace_id AND p.status = 'active'
      WHERE s.workspace_id = ? AND s.period_id IS NULL AND s.removed_at IS NULL ORDER BY p.program, s.profile_id`, [ws])).results;
    const holding = [...new Set(scope.filter((s) => s.component_id === comp.component_id).map((s) => s.profile_id))];
    const svc = serviceOf(comp);
    const replSvc = repl ? serviceOf(repl) : null;
    const programs = [];
    for (const profileId of holding) {
        const program = scope.find((s) => s.profile_id === profileId).program;
        const others = scope.filter((s) => s.profile_id === profileId && s.component_id !== comp.component_id).map((s) => byId.get(s.component_id)).filter((c) => !!c);
        const stillUsed = new Set(others.map(serviceOf).filter((x) => !!x));
        if (replSvc)
            stillUsed.add(replSvc);
        const removed = repl && svc && !stillUsed.has(svc) ? svc : null;
        const usedBefore = new Set([...others.map(serviceOf), svc].filter((x) => !!x));
        let cap = null;
        if (program === 'govramp') {
            if (removed && !verifiedForGovramp(services.get(removed)))
                cap = 'lifted';
            else if (replSvc && !usedBefore.has(replSvc) && !verifiedForGovramp(services.get(replSvc)))
                cap = 'added';
        }
        const lostReqs = new Map();
        if (removed) {
            for (const r of await listResponsibilities(db, ws, profileId)) {
                if (r.party === 'customer' || r.external_service_id !== removed)
                    continue;
                const reqId = requirementOf(r.objective_id);
                lostReqs.set(reqId, (lostReqs.get(reqId) ?? 0) + 1);
            }
        }
        programs.push({
            profile_id: profileId, program, label: getProgramManifest(program)?.shortLabel ?? program,
            provider_removed: removed ? { external_service_id: removed, name: services.get(removed)?.name ?? removed } : null,
            cap,
            lost: [...lostReqs.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([requirement_id, objectives]) => ({ requirement_id, label: requirementLabel(requirement_id), objectives })),
        });
    }
    const milestones = [];
    if (repl) {
        const by = input.due_on && /^\d{4}-\d{2}-\d{2}$/.test(input.due_on) ? new Date(`${input.due_on}T00:00:00.000Z`) : new Date(Date.parse(dayIso(now)) + 28 * DAY);
        const at = (days) => dayIso(new Date(by.getTime() - days * DAY)).slice(0, 10);
        const owner = await resolveDerivedOwner(db, ws, { cause_kind: 'transition_milestone', dedupe_key: 'preview', title: 'preview', consumers: [], program_id: holding[0] ?? null }, now);
        const add = async (m) => {
            const open = await openItemByDedupe(db, ws, m.dedupe_key);
            milestones.push({ ...m, owner_user_id: owner, already_planned: open ? { work_item_id: open.work_item_id, due_at: open.due_at } : null });
        };
        const replInBoundary = scope.some((s) => s.component_id === repl.component_id);
        if (!replInBoundary) {
            await add({ key: `stand_up:${comp.component_id}`, dedupe_key: replacementDedupeKey(comp.component_id, 'stand_up'), profile_id: null, kind: 'stand_up', requirement_id: null,
                title: `Stand up ${repl.component_key}`, due_on: at(14) });
        }
        for (const p of programs) {
            for (const l of p.lost) {
                await add({ key: `loss:${comp.component_id}:${l.requirement_id}`, dedupe_key: replacementDedupeKey(comp.component_id, 'loss', l.requirement_id), profile_id: p.profile_id, kind: 'loss',
                    requirement_id: l.requirement_id, title: `Own ${l.label}${p.provider_removed ? ` (was ${p.provider_removed.name})` : ''} · ${p.label}`, due_on: at(7) });
            }
        }
        const removedName = programs.find((p) => p.provider_removed)?.provider_removed?.name ?? null;
        await add({ key: `switch:${comp.component_id}`, dedupe_key: replacementDedupeKey(comp.component_id, 'switch'), profile_id: null, kind: 'switch', requirement_id: null,
            title: removedName ? `Move data off ${removedName} and switch ${comp.component_key} to ${repl.component_key}` : `Switch ${comp.component_key} to ${repl.component_key}`, due_on: at(0) });
    }
    const name = (c) => { const s = serviceOf(c); return s ? services.get(s)?.name ?? null : null; };
    return {
        component: { component_id: comp.component_id, component_key: comp.component_key, provider: name(comp) },
        replacement: repl ? { component_id: repl.component_id, component_key: repl.component_key, provider: name(repl) } : null,
        programs,
        milestones,
    };
}
/** Add the replacement's milestones (work items). Milestones already planned are not added twice. */
export async function planComponentReplacement(db, input) {
    if (!input.actor_user_id || input.actor_user_id.startsWith('system:'))
        throw new BoundaryError('invalid', 'This step is a person\'s decision.');
    const now = input.now ?? new Date();
    const preview = await previewComponentReplacement(db, { ...input, now });
    if (!preview.milestones.length)
        throw new BoundaryError('invalid', 'Choose a replacement component first.');
    const overrides = new Map((input.milestones ?? []).map((m) => [m.key, m]));
    for (const k of overrides.keys())
        if (!preview.milestones.some((m) => m.key === k))
            throw new BoundaryError('invalid', `Unknown milestone ${k}.`);
    // Every date and owner is checked before anything is written (no partial plan).
    const todo = [];
    for (const m of preview.milestones) {
        if (m.already_planned)
            continue;
        const o = overrides.get(m.key);
        const due = o?.due_on ?? m.due_on;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(due))
            throw new BoundaryError('invalid', 'due_on must be a date (YYYY-MM-DD).');
        const owner = o?.owner_user_id ?? m.owner_user_id;
        if (!owner)
            throw new BoundaryError('invalid', 'No eligible member can own the milestone; choose an owner.');
        await assertAssignable(db, { workspace_id: input.workspace_id, user_id: owner, actor_user_id: input.actor_user_id, subjects: [], now });
        todo.push({ m, due, owner });
    }
    const created = [];
    for (const { m, due, owner } of todo) {
        const consumers = m.profile_id ? [{ kind: 'program', id: m.profile_id }] : preview.programs.map((p) => ({ kind: 'program', id: p.profile_id }));
        try {
            const w = await createWorkItem(db, {
                workspace_id: input.workspace_id, kind: 'task', title: m.title.slice(0, 300),
                detail: `Replacing ${preview.component.component_key} with ${preview.replacement.component_key}. Planned-change work: a milestone, not a POA&M item.`,
                due_at: `${due}T00:00:00.000Z`, source_kind: REPLACEMENT_SOURCE_KIND, source_id: preview.component.component_id, dedupe_key: m.dedupe_key,
                owner_user_id: owner, consumers, actor_user_id: input.actor_user_id, now,
            });
            created.push(w.work_item_id);
        }
        catch (err) {
            if (!(err instanceof WorkItemDuplicateError))
                throw err;
        }
    }
    return { preview: await previewComponentReplacement(db, { ...input, now }), created };
}
//# sourceMappingURL=transition-milestone-service.js.map